import { AppTopBar } from '../components/AppTopBar';
import { isFilterMultiSelectNone } from '../components/filterMultiSelectUtils';
import { PlantafelView } from '../components/plantafel/PlantafelView';
import { PlantafelMapView } from '../components/plantafel/PlantafelMapView';
import { ActivityList } from '../components/plantafel/ActivityList';
import { ActivityListToolbar } from '../components/plantafel/ActivityListToolbar';
import { ActivityBulkActionBar } from '../components/plantafel/ActivityBulkActionBar';
import {
  effectiveActivityAddress,
  formatListEnd,
  formatListStart,
  getTypeLabel,
  listActivitySortValue,
  schedulingStatusLabel,
  type ListSortColumn,
} from '../components/plantafel/activityListFormat';
import { PlantafelDetailDrawer } from '../components/plantafel/PlantafelDetailDrawer';
import { PlantafelAbsenceDrawer } from '../components/plantafel/PlantafelAbsenceDrawer';
import type {
  AbsenceKind,
  BoardActivity,
  BoardEmployee,
  SchedulingStatus,
} from '../components/plantafel/plantafelTypes';
import {
  buildPlantafelTypeFilterOptions,
  PLANTAFEL_ORPHAN_LEAD_ID,
  plantafelTypeFilterAbsenceId,
  plantafelTypeFilterActivityId,
  SCHEDULING_STATUS_FILTER_OPTIONS,
} from '../components/plantafel/plantafelTypes';
import {
  absenceRowToDraft,
  absenceRowToDueEnd,
  mergeAbsenceBlocksForList,
  type CalendarListAbsenceBlock,
  type CalendarListAbsenceRow,
} from '../components/plantafel/calendarListAbsences';
import type { PlantafelAbsenceDraft } from '../components/plantafel/plantafelAbsenceMapping';
import { FALLBACK_ACTIVITY_TYPES } from '../utils/activityIcons';
import { localDayEndIso, localDayStartIso, isoToLocalDateAndTime, localWallTimeToIso, formatWallClockYmd, instantToHourFraction, isoToLocalHourFraction } from '../utils/activityDateTime';
import { useCompanyTimezone } from '../hooks/useCompanyTimezone';
import { TRADE_STYLES, categoryToTradeKey } from '../utils/categoryToTrade';
import { parseEmployeesListResponse } from '../utils/parseEmployeesApi';
import {
  EMPTY_CALENDAR_FILTERS,
  pruneFilterSelection,
  readCalendarFilters,
  readInitialCalendarFilters,
  writeCalendarFilters,
  type CalendarSharedFilters,
} from '../utils/calendarFiltersStorage';
import {
  buildCalendarListCsv,
  calendarListExportFilename,
  downloadCalendarListCsv,
} from '../utils/calendarListExport';
import { leadSelectSiteFieldsFromApi } from '../utils/leadSelectOption';
import { fetchMe } from '../lib/meClient';
import { useCapabilities } from '../hooks/useCapabilities';
import { AppSidebar } from '../components/AppSidebar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { AppToastViewport } from '../components/AppToastViewport';
import { useToasts } from '../hooks/useToasts';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React from 'react';
import { PageLoadingState } from '../components/LoadingSpinner';

const PLANTAFEL_EMPLOYEE_COLORS = ['#16a34a', '#2563eb', '#d97706', '#7c3aed', '#dc2626', '#0d9488'];

type CalendarEmployeeListRow = {
  id: string;
  first_name: string;
  last_name: string;
  color_idx: number;
  position: string;
  email: string;
};

type PlantafelEmployeeDisplay = {
  id: string;
  name: string;
  initials: string;
  color_idx: number;
  color: string;
  position: string;
};

function normalizePlantafelColorIdx(colorIdx: number): number {
  return (
    ((Number(colorIdx) % PLANTAFEL_EMPLOYEE_COLORS.length) + PLANTAFEL_EMPLOYEE_COLORS.length) %
    PLANTAFEL_EMPLOYEE_COLORS.length
  );
}

function plantafelEmployeeDisplayFromRow(e: CalendarEmployeeListRow): PlantafelEmployeeDisplay {
  const name = `${e.first_name} ${e.last_name}`.trim() || e.email || 'Mitarbeiter';
  const color_idx = normalizePlantafelColorIdx(e.color_idx);
  const f = e.first_name.trim().charAt(0);
  const l = e.last_name.trim().charAt(0);
  const initials =
    f || l ? `${f}${l}`.toUpperCase() : (e.email.trim().charAt(0).toUpperCase() || '?');
  return {
    id: e.id,
    name,
    initials,
    color_idx,
    color: PLANTAFEL_EMPLOYEE_COLORS[color_idx],
    position: e.position,
  };
}
const LIST_PAGE_SIZE = 100;

const DEFAULT_ACTIVITY_TYPE_ID = FALLBACK_ACTIVITY_TYPES[0]?.id ?? 'call';

type ScheduledActivity = {
  id: string;
  lead_id: string | null;
  title: string;
  activity_type: string;
  due_at: string | null;
  end_at: string | null;
  priority: string | null;
  notes: string | null;
  done: boolean;
  created_at: string;
  lead_display_id: string | null;
  lead_name: string | null;
  lead_title: string | null;
  lead_status: string | null;
  lead_category?: string | null;
  scheduling_status?: string;
  site_address?: string | null;
  activity_site_address?: string | null;
  lead_site_address?: string | null;
  lead_site_latitude?: number | null;
  lead_site_longitude?: number | null;
  lead_phone?: string | null;
  checklist_id?: string | null;
  series_id?: string | null;
  recurrence?: unknown;
  original_start_at?: string | null;
  series_exception?: boolean;
  assignee_users?: { user_id: string; display_name: string | null; email: string | null }[];
  assignee_employees?: { id: string; first_name: string; last_name: string; color_idx?: number }[];
  /** List-view marker: absence rows come from time entries, not lead_activities. */
  list_kind?: 'activity' | 'absence';
  absence_kind?: AbsenceKind;
  absence_entry_id?: string | null;
  absence_date_from?: string;
  absence_date_to?: string;
};

type InstanceMemberOption = { user_id: string; email: string; display_name: string | null };

type LeadOption = {
  id: string;
  display_id: string;
  name: string;
  lead_title?: string;
  created_at?: string;
  bestellnummer?: string | null;
  site_contact_name?: string | null;
  site_address?: string | null;
};

type ActivityTypeOption = {
  id: string;
  label: string;
  icon: string;
  default_checklist_id?: string | null;
};

type QuickAddState = {
  x: number;
  y: number;
  dayIndex: number;
  startMin: number;
  durationMin: number;
  title: string;
  activityType: string;
};

function addDays(base: Date, offset: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + offset);
  return d;
}

function getWeekStart(date: Date): Date {
  const day = (date.getDay() + 6) % 7; // Monday first
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - day);
  return start;
}

function absenceRowsToScheduled(
  rows: CalendarListAbsenceRow[],
  employees: CalendarEmployeeListRow[],
  timeZone: string,
): ScheduledActivity[] {
  const byId = new Map(employees.map((e) => [e.id, e]));
  return rows.map((row) => {
    const emp = byId.get(row.employee_id);
    const { due_at, end_at } = absenceRowToDueEnd(row, timeZone);
    return {
      id: row.id,
      lead_id: null,
      title: row.title,
      activity_type: row.absence_kind,
      due_at,
      end_at,
      priority: null,
      notes: null,
      done: false,
      created_at: due_at,
      lead_display_id: null,
      lead_name: null,
      lead_title: null,
      lead_status: null,
      scheduling_status: 'bestaetigt',
      assignee_users: [],
      assignee_employees: emp
        ? [
            {
              id: emp.id,
              first_name: emp.first_name,
              last_name: emp.last_name,
              color_idx: emp.color_idx,
            },
          ]
        : [],
      list_kind: 'absence' as const,
      absence_kind: row.absence_kind,
      absence_entry_id: row.entry_id,
      absence_date_from: row.date_from,
      absence_date_to: row.date_to,
    };
  });
}

/** Same project key as Plantafel filter options (`customer · title`). */
function scheduledActivityProjectKey(
  a: Pick<ScheduledActivity, 'lead_name' | 'lead_title' | 'title'>,
): string {
  const customer = String(a.lead_name ?? '').trim() || '—';
  const title = String(a.lead_title ?? a.title ?? '').trim() || 'Projekt';
  return `${customer} · ${title}`;
}

function scheduledToBoardActivity(
  activity: ScheduledActivity,
  employeesList: { id: string; color_idx: number; position: string }[],
): BoardActivity {
  const scheduling = (activity.scheduling_status ?? 'geplant') as SchedulingStatus;
  const activitySiteAddress =
    String(activity.activity_site_address ?? activity.site_address ?? '').trim() || null;
  return {
    id: activity.id,
    lead_id: activity.lead_id,
    title: activity.title,
    activity_type: activity.activity_type,
    due_at: activity.due_at,
    end_at: activity.end_at,
    priority: activity.priority,
    notes: activity.notes,
    checklist_id: activity.checklist_id ?? null,
    done: activity.done,
    scheduling_status: scheduling,
    primary_employee_id: activity.assignee_employees?.[0]?.id ?? null,
    lead_display_id: activity.lead_display_id,
    lead_name: activity.lead_name,
    lead_title: activity.lead_title,
    lead_category: activity.lead_category ?? null,
    activity_site_address: activitySiteAddress,
    lead_site_address: activity.lead_site_address ?? null,
    lead_site_latitude: activity.lead_site_latitude ?? null,
    lead_site_longitude: activity.lead_site_longitude ?? null,
    lead_phone: activity.lead_phone ?? null,
    series_id: activity.series_id ?? null,
    recurrence: activity.recurrence,
    original_start_at: activity.original_start_at ?? null,
    series_exception: Boolean(activity.series_exception),
    assignee_users: (activity.assignee_users ?? []).map((u) => ({
      user_id: u.user_id,
      display_name: u.display_name,
      email: u.email,
    })),
    assignee_employees: (activity.assignee_employees ?? []).map((e) => {
      const row = employeesList.find((x) => x.id === e.id);
      return {
        id: e.id,
        first_name: e.first_name,
        last_name: e.last_name,
        color_idx: row?.color_idx ?? 0,
        position: row?.position ?? '',
      };
    }),
  };
}

export function CalendarPage() {
  const timeZone = useCompanyTimezone();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useParams<{ mode?: string }>();
  const instancePrefix = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    return match ? match[0] : '/i/default';
  }, [location.pathname]);
  const instanceId = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/([^/]+)/);
    return match?.[1] ?? 'default';
  }, [location.pathname]);
  const [calendarFilters, setCalendarFilters] = React.useState<CalendarSharedFilters>(() =>
    readInitialCalendarFilters(instanceId),
  );
  const calendarFiltersRef = React.useRef(calendarFilters);
  const calendarFiltersTouchedRef = React.useRef(false);
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);
  const currentUserIdRef = React.useRef<string | null>(null);
  const [activities, setActivities] = React.useState<ScheduledActivity[]>([]);
  const [leads, setLeads] = React.useState<LeadOption[]>([]);
  const [activityTypes, setActivityTypes] = React.useState<ActivityTypeOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [weekStart, setWeekStart] = React.useState(() => getWeekStart(new Date()));
  const [showListCreate, setShowListCreate] = React.useState(false);
  const [isSavingActivity, setIsSavingActivity] = React.useState(false);
  const [activityError, setActivityError] = React.useState<string | null>(null);
  const [editingActivityId, setEditingActivityId] = React.useState<string | null>(null);
  const [quickAdd, setQuickAdd] = React.useState<QuickAddState | null>(null);
  const [showWeekPicker, setShowWeekPicker] = React.useState(false);
  const [pickerMonth, setPickerMonth] = React.useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [now, setNow] = React.useState(new Date());
  const weekGridRef = React.useRef<HTMLDivElement | null>(null);
  const [instanceMembers, setInstanceMembers] = React.useState<InstanceMemberOption[]>([]);
  const [employeesList, setEmployeesList] = React.useState<CalendarEmployeeListRow[]>([]);
  const [listPage, setListPage] = React.useState(0);
  const [listSort, setListSort] = React.useState<{ column: ListSortColumn; dir: 'asc' | 'desc' }>({
    column: 'start',
    dir: 'asc',
  });
  const listScrollRef = React.useRef<HTMLDivElement>(null);
  const [listAbsenceBlocks, setListAbsenceBlocks] = React.useState<CalendarListAbsenceBlock[]>([]);
  const [listAbsenceDrawer, setListAbsenceDrawer] = React.useState<PlantafelAbsenceDraft | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [deleteTargets, setDeleteTargets] = React.useState<ScheduledActivity[]>([]);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { toasts, pushToast, dismissToast } = useToasts();
  const { can } = useCapabilities();
  // Projektliste und Mitarbeiterliste gehören anderen Modulen; ohne deren Leserecht sperrt der Server.
  const canViewProjects = can('web.projekte.view');
  const canViewEmployees = can('web.hr.view');
  const viewMode: 'calendar' | 'map' | 'list' = mode === 'list' ? 'list' : mode === 'map' ? 'map' : 'calendar';

  React.useEffect(() => {
    calendarFiltersRef.current = calendarFilters;
  }, [calendarFilters]);

  React.useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const updateCalendarFilters = React.useCallback(
    (patch: Partial<CalendarSharedFilters> | ((prev: CalendarSharedFilters) => CalendarSharedFilters)) => {
      calendarFiltersTouchedRef.current = true;
      setCalendarFilters((prev) => {
        const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
        calendarFiltersRef.current = next;
        writeCalendarFilters(currentUserId, instanceId, next);
        return next;
      });
    },
    [currentUserId, instanceId],
  );

  React.useEffect(() => {
    let cancelled = false;
    // Never carry filters from the previous instance into the new one.
    calendarFiltersTouchedRef.current = false;
    const knownUserId = currentUserIdRef.current;
    if (knownUserId) {
      const stored = readCalendarFilters(knownUserId, instanceId);
      setCalendarFilters(stored);
      calendarFiltersRef.current = stored;
    } else {
      const stored = readInitialCalendarFilters(instanceId);
      setCalendarFilters(stored);
      calendarFiltersRef.current = stored;
    }

    void (async () => {
      try {
        const data = await fetchMe();
        const userId = String(data?.user?.id ?? '').trim();
        if (!userId || cancelled) return;
        setCurrentUserId(userId);
        if (calendarFiltersTouchedRef.current) {
          // User already edited during hydration — keep edits and persist under this key.
          writeCalendarFilters(userId, instanceId, calendarFiltersRef.current);
          return;
        }
        const stored = readCalendarFilters(userId, instanceId);
        if (cancelled) return;
        setCalendarFilters(stored);
        calendarFiltersRef.current = stored;
        writeCalendarFilters(userId, instanceId, stored);
      } catch {
        // keep current filters
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  const listFilters = {
    mitarbeiter: calendarFilters.mitarbeiter,
    projekt: calendarFilters.projekt,
    typ: calendarFilters.typ,
    status: calendarFilters.status,
  };
  const listSearch = calendarFilters.search;
  const listDateRange = {
    from: calendarFilters.dateFrom,
    to: calendarFilters.dateTo,
  };

  React.useEffect(() => {
    if (mode && mode !== 'calendar' && mode !== 'list' && mode !== 'map') {
      navigate(`${instancePrefix}/calendar/calendar`, { replace: true });
    }
  }, [mode, navigate, instancePrefix]);

  const loadScheduledActivities = React.useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setIsLoading(true);
    fetch('/api/activities/scheduled')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setActivities(Array.isArray(data) ? data : []))
      .catch(() => setActivities([]))
      .finally(() => {
        if (!opts?.silent) setIsLoading(false);
      });
  }, []);

  React.useEffect(() => {
    if (viewMode === 'calendar') return;
    loadScheduledActivities();
  }, [loadScheduledActivities, viewMode]);

  const listAbsencesFetchRange = React.useMemo(() => {
    if (listDateRange.from && listDateRange.to) {
      const from = new Date(`${listDateRange.from}T12:00:00`);
      const to = new Date(`${listDateRange.to}T12:00:00`);
      return { from, to };
    }
    const now = new Date();
    return { from: addDays(now, -120), to: addDays(now, 365) };
  }, [listDateRange.from, listDateRange.to]);

  const loadListAbsences = React.useCallback(() => {
    if (viewMode !== 'list') return;
    const { from, to } = listAbsencesFetchRange;
    const url = `/api/plantafel/board?from=${encodeURIComponent(localDayStartIso(from, timeZone))}&to=${encodeURIComponent(localDayEndIso(to, timeZone))}`;
    fetch(url, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const blocks = Array.isArray(data?.absence_blocks) ? data.absence_blocks : [];
        setListAbsenceBlocks(
          blocks
            .map((b: Record<string, unknown>) => ({
              id: String(b.id ?? ''),
              employee_id: String(b.employee_id ?? ''),
              entry_date: String(b.entry_date ?? '').slice(0, 10),
              entry_type: String(b.entry_type ?? ''),
              absence_kind: (String(b.absence_kind ?? 'other') as AbsenceKind | 'other'),
              holiday_name: (b.holiday_name as string | null) ?? null,
              start_time: (b.start_time as string | null) ?? null,
              end_time: (b.end_time as string | null) ?? null,
            }))
            .filter((b: CalendarListAbsenceBlock) => b.id && b.employee_id && b.entry_date),
        );
      })
      .catch(() => setListAbsenceBlocks([]));
  }, [viewMode, listAbsencesFetchRange, timeZone]);

  React.useEffect(() => {
    loadListAbsences();
  }, [loadListAbsences]);

  const listAbsenceActivities = React.useMemo(
    () => absenceRowsToScheduled(mergeAbsenceBlocksForList(listAbsenceBlocks), employeesList, timeZone),
    [listAbsenceBlocks, employeesList, timeZone],
  );
  React.useEffect(() => {
    if (viewMode === 'calendar' || !canViewEmployees) return;
    fetch('/api/employees')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const rows = Array.isArray((data as { items?: unknown })?.items)
          ? ((data as { items: unknown[] }).items ?? [])
          : (parseEmployeesListResponse(data) as unknown[]);
        setEmployeesList(
          rows.map((e) => {
            const row = e as {
              id?: string;
              first_name?: string;
              last_name?: string;
              color_idx?: number;
              position?: string;
              email?: string;
            };
            return {
              id: String(row.id ?? ''),
              first_name: String(row.first_name ?? ''),
              last_name: String(row.last_name ?? ''),
              color_idx: Number(row.color_idx ?? 0),
              position: String(row.position ?? '').trim(),
              email: String(row.email ?? '').trim(),
            };
          }).filter((e) => e.id.length > 0),
        );
      })
      .catch(() => setEmployeesList([]));
  }, [viewMode, canViewEmployees]);

  React.useEffect(() => {
    fetch('/api/me/members', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((data) => {
        const list = Array.isArray(data?.members) ? data.members : [];
        setInstanceMembers(
          list
            .map((m: { user_id?: string; email?: string; display_name?: string | null }) => ({
              user_id: String(m.user_id ?? ''),
              email: String(m.email ?? ''),
              display_name: m.display_name ?? null,
            }))
            .filter((m: InstanceMemberOption) => m.user_id.length > 0),
        );
      })
      .catch(() => setInstanceMembers([]));
  }, []);

  React.useEffect(() => {
    if (!canViewProjects) return;
    fetch('/api/leads')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const options = Array.isArray(data)
          ? data
              .map((l: any) => {
                const site = leadSelectSiteFieldsFromApi(l);
                return {
                  id: String(l.id ?? ''),
                  display_id: String(l.display_id ?? ''),
                  name: String(l.name ?? ''),
                  lead_title: String(l.lead_title ?? ''),
                  created_at: String(l.created_at ?? ''),
                  bestellnummer: site.bestellnummer,
                  site_contact_name: site.site_contact_name,
                  site_address: site.site_address,
                };
              })
              .filter((l: LeadOption) => l.id.length > 0)
          : [];
        setLeads(options);
      })
      .catch(() => setLeads([]));
  }, [canViewProjects]);

  React.useEffect(() => {
    fetch('/api/settings/activity-types')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const options = (Array.isArray(data) ? data : [])
          .slice()
          .sort((a: ActivityTypeOption, b: ActivityTypeOption) =>
            a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }),
          );
        setActivityTypes(options);
      })
      .catch(() => setActivityTypes([]));
  }, []);

  const handleLeadCreated = React.useCallback((lead: LeadOption) => {
    setLeads((prev) => (prev.some((l) => l.id === lead.id) ? prev : [lead, ...prev]));
  }, []);

  React.useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(interval);
  }, []);

  const editingActivity = React.useMemo(
    () => activities.find((a) => a.id === editingActivityId) ?? null,
    [activities, editingActivityId],
  );

  const listDrawerLeads = React.useMemo(() => {
    if (!editingActivity?.lead_id) return leads;
    const curId = editingActivity.lead_id;
    if (leads.some((o) => o.id === curId)) return leads;
    return [
      ...leads,
      {
        id: curId,
        display_id: String(editingActivity.lead_display_id ?? '').trim() || curId,
        name: String(editingActivity.lead_name ?? ''),
      },
    ];
  }, [editingActivity, leads]);

  const plantafelEmployeeDisplay = React.useMemo(
    () => employeesList.map(plantafelEmployeeDisplayFromRow),
    [employeesList],
  );

  const listBoardEmployees = React.useMemo(
    (): BoardEmployee[] =>
      plantafelEmployeeDisplay.map((e) => ({
        id: e.id,
        name: e.name,
        initials: e.initials,
        role: e.position || '',
        color_idx: e.color_idx,
        color: e.color,
        capacity_hours: 0,
        expected_hours_per_day: 0,
      })),
    [plantafelEmployeeDisplay],
  );

  const listDrawerActivity = React.useMemo(
    () => (editingActivity ? scheduledToBoardActivity(editingActivity, employeesList) : null),
    [editingActivity, employeesList],
  );

  React.useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      if (showWeekPicker) {
        setShowWeekPicker(false);
        return;
      }
      if (quickAdd) {
        setQuickAdd(null);
        return;
      }
      if (listAbsenceDrawer) {
        setListAbsenceDrawer(null);
        return;
      }
      if (editingActivityId) {
        setEditingActivityId(null);
        return;
      }
      if (showListCreate) {
        setShowListCreate(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [quickAdd, editingActivityId, showListCreate, showWeekPicker, listAbsenceDrawer]);

  React.useEffect(() => {
    if (!quickAdd) return;
    const onMouseDown = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-quick-add="true"]')) return;
      setQuickAdd(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [quickAdd]);

  React.useEffect(() => {
    if (!showWeekPicker) return;
    const onMouseDown = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-week-picker="true"]') || target.closest('[data-week-picker-trigger="true"]')) return;
      setShowWeekPicker(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showWeekPicker]);

  const DAY_START_HOUR = 5;
  const DAY_END_HOUR = 22;
  const HOUR_HEIGHT = 56;
  const dayHours = React.useMemo(
    () => Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i),
    []
  );
  const weekDays = React.useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = React.useMemo(() => addDays(weekStart, 6), [weekStart]);
  const calendarBodyHeight = (DAY_END_HOUR - DAY_START_HOUR) * HOUR_HEIGHT;

  const listFilterEmployees = React.useMemo(
    () =>
      plantafelEmployeeDisplay.map((e) => ({
        id: e.id,
        name: e.name,
        color_idx: e.color_idx,
        avatarColor: e.color,
        initials: e.initials,
        subtitle: e.position || undefined,
      })),
    [plantafelEmployeeDisplay],
  );

  const listFilterProjects = React.useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        leadId: string;
        title: string;
        customer: string;
        trade: keyof typeof TRADE_STYLES;
      }
    >();
    for (const a of activities) {
      if (!a.lead_id) continue;
      const key = scheduledActivityProjectKey(a);
      if (map.has(key)) continue;
      map.set(key, {
        key,
        leadId: a.lead_id,
        title: String(a.lead_title ?? a.title ?? '').trim() || 'Projekt',
        customer: String(a.lead_name ?? '').trim() || '—',
        trade: categoryToTradeKey(a.lead_category),
      });
    }
    return [...map.values()];
  }, [activities]);

  React.useEffect(() => {
    const typeOptions = buildPlantafelTypeFilterOptions(
      activityTypes.length > 0 ? activityTypes : FALLBACK_ACTIVITY_TYPES,
    );
    const validTyp = new Set(typeOptions.map((o) => o.id));
    const validStatus = new Set<string>(SCHEDULING_STATUS_FILTER_OPTIONS.map((o) => o.id));
    const validMitarbeiter = new Set(employeesList.map((e) => e.id));

    setCalendarFilters((prev) => {
      let next = prev;
      const typ = pruneFilterSelection(prev.typ, validTyp);
      if (typ !== prev.typ) next = { ...next, typ };
      const status = pruneFilterSelection(prev.status, validStatus);
      if (status !== prev.status) next = { ...next, status };
      if (validMitarbeiter.size > 0) {
        const mitarbeiter = pruneFilterSelection(prev.mitarbeiter, validMitarbeiter);
        if (mitarbeiter !== prev.mitarbeiter) next = { ...next, mitarbeiter };
      }
      // Only prune projects once list/map activity projects are known; always keep
      // Plantafel's orphan sentinel so "__ohne_projekt__" is not wiped to NONE.
      if (listFilterProjects.length > 0) {
        const validProjekt = new Set(listFilterProjects.map((p) => p.leadId));
        validProjekt.add(PLANTAFEL_ORPHAN_LEAD_ID);
        const projekt = pruneFilterSelection(prev.projekt, validProjekt);
        if (projekt !== prev.projekt) next = { ...next, projekt };
      }
      if (next === prev) return prev;
      calendarFiltersRef.current = next;
      writeCalendarFilters(currentUserId, instanceId, next);
      return next;
    });
  }, [activityTypes, employeesList, listFilterProjects, currentUserId, instanceId]);

  const listFilteredActivities = React.useMemo(() => {
    let rows: ScheduledActivity[] = [
      ...activities.map((a) => ({ ...a, list_kind: a.list_kind ?? ('activity' as const) })),
      ...listAbsenceActivities,
    ];

    if (listDateRange.from && listDateRange.to) {
      const { from, to } = listDateRange;
      rows = rows.filter((a) => {
        if (a.list_kind === 'absence') {
          const start = (a.absence_date_from ?? a.due_at?.slice(0, 10) ?? '');
          const end = (a.absence_date_to ?? a.end_at?.slice(0, 10) ?? start);
          if (!start) return false;
          return start <= to && end >= from;
        }
        if (!a.due_at) return false;
        const day = a.due_at.slice(0, 10);
        const endDay = a.end_at ? a.end_at.slice(0, 10) : day;
        return day <= to && endDay >= from;
      });
    }

    const q = listSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) => {
        const typeLabel = getTypeLabel(a.activity_type, activityTypes);
        const haystack = [
          a.title,
          a.lead_name,
          a.lead_display_id,
          a.lead_title,
          a.notes,
          typeLabel,
          ...(a.assignee_users ?? []).map((u) => u.display_name?.trim() || u.email || ''),
          ...(a.assignee_employees ?? []).map((e) => `${e.first_name} ${e.last_name}`.trim()),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (isFilterMultiSelectNone(listFilters.mitarbeiter)) {
      rows = [];
    } else if (listFilters.mitarbeiter.length > 0) {
      const allowed = new Set(listFilters.mitarbeiter);
      rows = rows.filter((a) => (a.assignee_employees ?? []).some((e) => allowed.has(e.id)));
    }

    if (rows.length > 0 && isFilterMultiSelectNone(listFilters.projekt)) {
      rows = [];
    } else if (listFilters.projekt.length > 0) {
      const allowed = new Set(listFilters.projekt);
      rows = rows.filter((a) => {
        if (a.list_kind === 'absence') return false;
        const leadId = a.lead_id || PLANTAFEL_ORPHAN_LEAD_ID;
        return allowed.has(leadId);
      });
    }

    if (rows.length > 0 && isFilterMultiSelectNone(listFilters.typ)) {
      rows = [];
    } else if (listFilters.typ.length > 0) {
      const allowed = new Set(listFilters.typ);
      rows = rows.filter((a) => {
        if (a.list_kind === 'absence' && a.absence_kind) {
          if (a.absence_kind === 'feiertag') return false;
          return allowed.has(plantafelTypeFilterAbsenceId(a.absence_kind));
        }
        return allowed.has(plantafelTypeFilterActivityId(a.activity_type));
      });
    }

    if (rows.length > 0 && isFilterMultiSelectNone(listFilters.status)) {
      rows = [];
    } else if (listFilters.status.length > 0) {
      const allowed = new Set(listFilters.status);
      rows = rows.filter((a) => {
        if (a.list_kind === 'absence') return true;
        return allowed.has(a.scheduling_status ?? 'geplant');
      });
    }

    return rows;
  }, [
    activities,
    listAbsenceActivities,
    listFilterProjects,
    listFilters,
    listDateRange,
    listSearch,
    activityTypes,
  ]);

  const weekActivities = React.useMemo(() => {
    const startTs = weekStart.getTime();
    const endTs = addDays(weekStart, 7).getTime();
    const entries: Array<
      ScheduledActivity & {
        dayIndex: number;
        top: number;
        height: number;
        column: number;
        totalColumns: number;
      }
    > = [];

    for (const activity of activities) {
      if (!activity.due_at) continue;
      const start = new Date(activity.due_at);
      const startTime = start.getTime();
      if (startTime < startTs || startTime >= endTs) continue;

      const end = activity.end_at ? new Date(activity.end_at) : new Date(startTime + 60 * 60 * 1000);
      const startMin = Math.round(isoToLocalHourFraction(activity.due_at, timeZone) * 60);
      const endMinRaw = Math.round(isoToLocalHourFraction(end.toISOString(), timeZone) * 60);
      const minStart = DAY_START_HOUR * 60;
      const minEnd = DAY_END_HOUR * 60;
      const topMin = Math.max(minStart, startMin);
      const bottomMin = Math.max(topMin + 15, Math.min(minEnd, Math.max(endMinRaw, topMin + 15)));
      if (bottomMin <= minStart || topMin >= minEnd) continue;

      const activityDateKey = formatWallClockYmd(start, timeZone);
      let dayIndex = -1;
      for (let i = 0; i < 7; i++) {
        if (formatWallClockYmd(addDays(weekStart, i), timeZone) === activityDateKey) {
          dayIndex = i;
          break;
        }
      }
      if (dayIndex < 0 || dayIndex > 6) continue;

      entries.push({
        ...activity,
        dayIndex,
        top: ((topMin - minStart) / 60) * HOUR_HEIGHT,
        height: Math.max(26, ((bottomMin - topMin) / 60) * HOUR_HEIGHT),
        column: 0,
        totalColumns: 1,
      });
    }

    entries.sort((a, b) => {
      if (a.top !== b.top) return a.top - b.top;
      return String(a.id).localeCompare(String(b.id));
    });

    // Assign columns within each day to prevent overlapping activities from hiding each other
    const byDay = new Map<number, typeof entries>();
    for (const e of entries) {
      if (!byDay.has(e.dayIndex)) byDay.set(e.dayIndex, []);
      byDay.get(e.dayIndex)!.push(e);
    }
    for (const dayEntries of byDay.values()) {
      const cols: number[] = new Array(dayEntries.length).fill(0);
      for (let i = 0; i < dayEntries.length; i++) {
        const usedCols = new Set<number>();
        for (let j = 0; j < i; j++) {
          if (dayEntries[j].top < dayEntries[i].top + dayEntries[i].height &&
              dayEntries[j].top + dayEntries[j].height > dayEntries[i].top) {
            usedCols.add(cols[j]);
          }
        }
        let col = 0;
        while (usedCols.has(col)) col++;
        cols[i] = col;
      }
      for (let i = 0; i < dayEntries.length; i++) {
        let maxCol = cols[i];
        for (let j = 0; j < dayEntries.length; j++) {
          if (j === i) continue;
          if (dayEntries[j].top < dayEntries[i].top + dayEntries[i].height &&
              dayEntries[j].top + dayEntries[j].height > dayEntries[i].top) {
            maxCol = Math.max(maxCol, cols[j]);
          }
        }
        dayEntries[i].column = cols[i];
        dayEntries[i].totalColumns = maxCol + 1;
      }
    }

    return entries;
  }, [activities, weekStart, timeZone]);

  const listItems = React.useMemo(() => {
    const sorted = [...listFilteredActivities].sort((a, b) => {
      const av = listActivitySortValue(a, listSort.column, activityTypes);
      const bv = listActivitySortValue(b, listSort.column, activityTypes);
      let cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'de', { sensitivity: 'base' });
      if (cmp === 0) {
        const at = a.due_at ? new Date(a.due_at).getTime() : Number.MAX_SAFE_INTEGER;
        const bt = b.due_at ? new Date(b.due_at).getTime() : Number.MAX_SAFE_INTEGER;
        cmp = at - bt;
        if (cmp === 0) {
          cmp = a.title.localeCompare(b.title, 'de', { sensitivity: 'base' });
        }
      }
      return listSort.dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [listFilteredActivities, listSort, activityTypes]);

  const listPageCount = Math.max(1, Math.ceil(listItems.length / LIST_PAGE_SIZE));
  const listPageIndex = Math.min(listPage, listPageCount - 1);

  React.useEffect(() => {
    setListPage(0);
  }, [listSearch, listFilters, listDateRange, listSort]);

  const handleListSort = React.useCallback((column: ListSortColumn) => {
    setListSort((prev) =>
      prev.column === column
        ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: 'asc' },
    );
  }, []);

  React.useEffect(() => {
    setListPage((p) => Math.min(p, listPageCount - 1));
  }, [listPageCount]);

  const listPageItems = React.useMemo(() => {
    const start = listPageIndex * LIST_PAGE_SIZE;
    return listItems.slice(start, start + LIST_PAGE_SIZE);
  }, [listItems, listPageIndex]);

  const listRangeFrom = listItems.length === 0 ? 0 : listPageIndex * LIST_PAGE_SIZE + 1;
  const listRangeTo = Math.min(listItems.length, (listPageIndex + 1) * LIST_PAGE_SIZE);

  const goToListPage = React.useCallback((next: number) => {
    setListPage(next);
    listScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const hasActiveListFilters =
    Boolean(listSearch.trim()) ||
    Boolean(listDateRange.from || listDateRange.to) ||
    listFilters.mitarbeiter.length > 0 ||
    listFilters.projekt.length > 0 ||
    listFilters.typ.length > 0 ||
    listFilters.status.length > 0;

  const resetListFilters = React.useCallback(() => {
    updateCalendarFilters({ ...EMPTY_CALENDAR_FILTERS });
  }, [updateCalendarFilters]);

  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [
    listSearch,
    listDateRange.from,
    listDateRange.to,
    listFilters.mitarbeiter,
    listFilters.projekt,
    listFilters.typ,
    listFilters.status,
    listSort,
  ]);

  React.useEffect(() => {
    const valid = new Set(listItems.map((a) => a.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (valid.has(id)) next.add(id);
        else changed = true;
      }
      return changed || next.size !== prev.size ? next : prev;
    });
  }, [listItems]);

  const selectedListItems = React.useMemo(
    () => listItems.filter((a) => selectedIds.has(a.id)),
    [listItems, selectedIds],
  );

  const handleToggleSelected = React.useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleToggleAll = React.useCallback(
    (selected: boolean) => {
      setSelectedIds(selected ? new Set(listItems.map((a) => a.id)) : new Set());
    },
    [listItems],
  );

  const toCsvRows = React.useCallback(
    (rows: ScheduledActivity[]) =>
      rows.map((activity) => {
        const isAbsence = activity.list_kind === 'absence';
        return {
          start: formatListStart(activity.due_at, isAbsence, timeZone),
          end: formatListEnd(activity.due_at, activity.end_at, isAbsence, timeZone),
          title: activity.title,
          project:
            activity.lead_display_id && activity.lead_name
              ? `${activity.lead_display_id} - ${activity.lead_name}`
              : '',
          address: effectiveActivityAddress(activity) ?? '',
          projectLeads: (activity.assignee_users ?? [])
            .map((u) => u.display_name?.trim() || u.email)
            .filter(Boolean)
            .join(', '),
          crew: (activity.assignee_employees ?? [])
            .map((e) => `${e.first_name} ${e.last_name}`.trim())
            .filter(Boolean)
            .join(', '),
          type: getTypeLabel(activity.activity_type, activityTypes),
          status: isAbsence ? '' : schedulingStatusLabel(activity.scheduling_status, activity.done),
        };
      }),
    [activityTypes, timeZone],
  );

  const handleExportSelected = React.useCallback(() => {
    const rows = selectedListItems.length > 0 ? selectedListItems : listItems;
    if (rows.length === 0) return;
    const csv = buildCalendarListCsv(toCsvRows(rows));
    downloadCalendarListCsv(csv, calendarListExportFilename(listDateRange.from, listDateRange.to));
    pushToast(
      `${rows.length} ${rows.length === 1 ? 'Eintrag' : 'Einträge'} als CSV exportiert.`,
      'success',
    );
  }, [listDateRange.from, listDateRange.to, listItems, pushToast, selectedListItems, toCsvRows]);

  const handleConfirmDelete = React.useCallback(async () => {
    const targets = deleteTargets;
    if (targets.length === 0) return;
    setIsDeleting(true);

    try {
      const res = await fetch('/api/activities/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: targets.map((a) => a.id) }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        deleted?: number;
        ids?: string[];
        missing?: string[];
      };

      if (!res.ok && res.status !== 207) {
        throw new Error(body?.error || 'Aktivitäten konnten nicht gelöscht werden.');
      }

      const deletedIds = Array.isArray(body.ids) ? body.ids : [];
      const missingCount = Array.isArray(body.missing) ? body.missing.length : 0;

      if (deletedIds.length > 0) {
        const removed = new Set(deletedIds);
        setActivities((prev) => prev.filter((a) => !removed.has(a.id)));
        setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => !removed.has(id))));
        pushToast(
          deletedIds.length === 1
            ? 'Aktivität gelöscht.'
            : `${deletedIds.length} Aktivitäten gelöscht.`,
          'success',
        );
        void loadScheduledActivities({ silent: true });
      }

      if (missingCount > 0) {
        pushToast(
          missingCount === 1
            ? '1 Aktivität wurde nicht gefunden.'
            : `${missingCount} Aktivitäten wurden nicht gefunden.`,
          'error',
        );
      }

      if (deletedIds.length === 0 && missingCount === 0) {
        pushToast(body?.error || 'Keine Aktivitäten gelöscht.', 'error');
      }
    } catch (err) {
      console.error('Failed to bulk-delete activities', err);
      pushToast(
        err instanceof Error ? err.message : 'Aktivitäten konnten nicht gelöscht werden.',
        'error',
      );
    } finally {
      setIsDeleting(false);
      setDeleteTargets([]);
    }
  }, [deleteTargets, loadScheduledActivities, pushToast]);

  const requestBulkDelete = React.useCallback(() => {
    const activitiesOnly = selectedListItems.filter((a) => a.list_kind !== 'absence');
    const absenceCount = selectedListItems.length - activitiesOnly.length;
    if (absenceCount > 0) {
      pushToast(
        absenceCount === 1
          ? '1 Abwesenheit wird beim Löschen übersprungen.'
          : `${absenceCount} Abwesenheiten werden beim Löschen übersprungen.`,
        'info',
      );
    }
    if (activitiesOnly.length === 0) {
      pushToast('Keine löschbaren Aktivitäten in der Auswahl.', 'error');
      return;
    }
    setDeleteTargets(activitiesOnly);
  }, [pushToast, selectedListItems]);

  const deleteDialogTitle =
    deleteTargets.length > 1
      ? `${deleteTargets.length} Aktivitäten löschen?`
      : 'Aktivität löschen?';
  const deleteDialogDescription =
    deleteTargets.length > 1
      ? `${deleteTargets.length} Aktivitäten werden dauerhaft gelöscht.`
      : `„${deleteTargets[0]?.title || 'Aktivität'}“ wird dauerhaft gelöscht.`;

  const weekLabel = `${new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' }).format(
    weekStart
  )} - ${new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }).format(weekEnd)}`;
  const weekdayLabel = (date: Date) => new Intl.DateTimeFormat('de-DE', { weekday: 'short' }).format(date);
  const pickerMonthLabel = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(pickerMonth);
  const pickerMonthStart = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), 1);
  const pickerGridStart = addDays(pickerMonthStart, -((pickerMonthStart.getDay() + 6) % 7));
  const pickerDays = React.useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(pickerGridStart, i)), [pickerGridStart]);
  const weekdayHeaders = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const nowInWeek = now >= weekStart && now < addDays(weekStart, 7);
  const nowMin = Math.round(instantToHourFraction(now, timeZone) * 60);
  const nowTop = ((nowMin - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const showNowLine = nowInWeek && nowMin >= DAY_START_HOUR * 60 && nowMin <= DAY_END_HOUR * 60;
  const nowDayIndex = (() => {
    const nowKey = formatWallClockYmd(now, timeZone);
    for (let i = 0; i < 7; i++) {
      if (formatWallClockYmd(addDays(weekStart, i), timeZone) === nowKey) return i;
    }
    return -1;
  })();

  const handleQuickGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-activity-block="true"]') || target.closest('[data-quick-add="true"]')) return;
    if (quickAdd) {
      setQuickAdd(null);
      return;
    }
    if (!weekGridRef.current) return;
    const rect = weekGridRef.current.getBoundingClientRect();
    const localX = Math.max(0, Math.min(rect.width - 1, e.clientX - rect.left));
    const localY = Math.max(0, Math.min(rect.height - 1, e.clientY - rect.top));
    const dayIndex = Math.max(0, Math.min(6, Math.floor((localX / rect.width) * 7)));
    const minutesFromStart = Math.floor((localY / HOUR_HEIGHT) * 2) * 30;
    const startMin = Math.max(DAY_START_HOUR * 60, Math.min(DAY_END_HOUR * 60 - 30, DAY_START_HOUR * 60 + minutesFromStart));
    const panelWidth = 360;
    const panelHeight = 170;
    const popupX = Math.max(8, Math.min(rect.width - panelWidth - 8, localX - 40));
    const popupY = Math.max(8, Math.min(rect.height - panelHeight - 8, localY - 20));
    const defaultType = activityTypes[0]?.id ?? DEFAULT_ACTIVITY_TYPE_ID;
    setQuickAdd({
      x: popupX,
      y: popupY,
      dayIndex,
      startMin,
      durationMin: 30,
      title: getTypeLabel(defaultType, activityTypes),
      activityType: defaultType,
    });
  };

  const handleQuickAddCreate = async () => {
    if (!quickAdd) return;
    const day = addDays(weekStart, quickAdd.dayIndex);
    const date = formatWallClockYmd(day, timeZone);
    const hh = String(Math.floor(quickAdd.startMin / 60)).padStart(2, '0');
    const mm = String(quickAdd.startMin % 60).padStart(2, '0');
    const endMin = Math.min(DAY_END_HOUR * 60, quickAdd.startMin + quickAdd.durationMin);
    const eh = String(Math.floor(endMin / 60)).padStart(2, '0');
    const em = String(endMin % 60).padStart(2, '0');
    setIsSavingActivity(true);
    setActivityError(null);
    try {
      const res = await fetch('/api/activities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: null,
          title: quickAdd.title.trim() || getTypeLabel(quickAdd.activityType, activityTypes),
          activity_type: quickAdd.activityType || DEFAULT_ACTIVITY_TYPE_ID,
          due_at: localWallTimeToIso(date, `${hh}:${mm}`, timeZone),
          end_at: localWallTimeToIso(date, `${eh}:${em}`, timeZone),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(body?.error || 'Aktivität konnte nicht erstellt werden.');
      }
      await loadScheduledActivities();
      setQuickAdd(null);
    } catch (err) {
      setActivityError(err instanceof Error ? err.message : 'Aktivität konnte nicht erstellt werden.');
    } finally {
      setIsSavingActivity(false);
    }
  };

  const openEditActivity = (activity: ScheduledActivity) => {
    if (activity.list_kind === 'absence') {
      const due = activity.due_at ? isoToLocalDateAndTime(activity.due_at, timeZone) : null;
      const end = activity.end_at ? isoToLocalDateAndTime(activity.end_at, timeZone) : null;
      const sameDayTimed =
        Boolean(activity.due_at && activity.end_at) &&
        activity.absence_date_from === activity.absence_date_to &&
        due &&
        end &&
        (due.time !== '00:00' || end.time !== '00:00');
      const draft = absenceRowToDraft({
        id: activity.id,
        employee_id: activity.assignee_employees?.[0]?.id ?? '',
        date_from: activity.absence_date_from ?? due?.dateKey ?? '',
        date_to: activity.absence_date_to ?? activity.absence_date_from ?? due?.dateKey ?? '',
        entry_type: activity.activity_type,
        absence_kind: activity.absence_kind ?? 'urlaub',
        title: activity.title,
        entry_id: activity.absence_entry_id ?? null,
        start_time: sameDayTimed ? due!.time : null,
        end_time: sameDayTimed ? end!.time : null,
      });
      if (draft) {
        setEditingActivityId(null);
        setShowListCreate(false);
        setListAbsenceDrawer(draft);
      }
      return;
    }
    setListAbsenceDrawer(null);
    setShowListCreate(false);
    setEditingActivityId(activity.id);
  };

  return (
    <div className="h-screen flex bg-app-bg font-sans overflow-hidden">
      <AppSidebar active="calendar" />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title={viewMode === 'map' ? 'Karte' : 'Kalender'} />

        <div className="relative flex-1 min-h-0 overflow-hidden">
          {viewMode === 'calendar' ? (
            <PlantafelView
              instancePrefix={instancePrefix}
              leads={leads}
              activityTypes={activityTypes}
              instanceMembers={instanceMembers}
              onLeadCreated={handleLeadCreated}
              filters={{
                mitarbeiter: calendarFilters.mitarbeiter,
                projekt: calendarFilters.projekt,
                typ: calendarFilters.typ,
                status: calendarFilters.status,
              }}
              onFiltersChange={(next) => updateCalendarFilters(next)}
            />
          ) : viewMode === 'map' ? (
            <PlantafelMapView
              instancePrefix={instancePrefix}
              leads={leads}
              activityTypes={activityTypes}
              instanceMembers={instanceMembers}
              onLeadCreated={handleLeadCreated}
              employeeFilter={calendarFilters.mitarbeiter}
              onEmployeeFilterChange={(mitarbeiter) => updateCalendarFilters({ mitarbeiter })}
            />
          ) : isLoading ? (
            <div className="flex h-full items-center justify-center bg-app-surface">
              <PageLoadingState />
            </div>
          ) : (
            <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-app-surface">
              <ActivityListToolbar
                instancePrefix={instancePrefix}
                resultCount={listItems.length}
                dateRange={listDateRange}
                onDateRangeChange={(value) =>
                  updateCalendarFilters({ dateFrom: value.from, dateTo: value.to })
                }
                employees={listFilterEmployees}
                projects={listFilterProjects}
                activityTypes={activityTypes}
                mitarbeiter={listFilters.mitarbeiter}
                projekt={listFilters.projekt}
                typ={listFilters.typ}
                status={listFilters.status}
                search={listSearch}
                onMitarbeiterChange={(mitarbeiter) => updateCalendarFilters({ mitarbeiter })}
                onProjektChange={(projekt) => updateCalendarFilters({ projekt })}
                onTypChange={(typ) => updateCalendarFilters({ typ })}
                onStatusChange={(status) => updateCalendarFilters({ status })}
                onSearchChange={(search) => updateCalendarFilters({ search })}
                onResetFilters={resetListFilters}
                onCreateActivity={() => {
                  setListAbsenceDrawer(null);
                  setEditingActivityId(null);
                  setShowListCreate(true);
                }}
              />
              <ActivityList
                items={listItems}
                pageItems={listPageItems}
                activityTypes={activityTypes}
                timeZone={timeZone}
                sortColumn={listSort.column}
                sortDir={listSort.dir}
                onSort={handleListSort}
                selectedIds={selectedIds}
                onToggleSelected={handleToggleSelected}
                onToggleAll={handleToggleAll}
                onOpenItem={openEditActivity}
                hasActiveFilters={hasActiveListFilters}
                onResetFilters={resetListFilters}
                onCreateActivity={() => {
                  setListAbsenceDrawer(null);
                  setEditingActivityId(null);
                  setShowListCreate(true);
                }}
                pageIndex={listPageIndex}
                pageCount={listPageCount}
                rangeFrom={listRangeFrom}
                rangeTo={listRangeTo}
                pageSize={LIST_PAGE_SIZE}
                onPageChange={goToListPage}
                scrollRef={listScrollRef}
              />
              <ActivityBulkActionBar
                selectedCount={selectedListItems.length}
                onClear={() => setSelectedIds(new Set())}
                onExport={handleExportSelected}
                onDelete={requestBulkDelete}
                isDeleting={isDeleting}
              />

              {(listDrawerActivity || showListCreate) && !listAbsenceDrawer && (
                <PlantafelDetailDrawer
                  activity={showListCreate ? null : listDrawerActivity}
                  createDraft={null}
                  leads={listDrawerLeads}
                  activityTypes={activityTypes}
                  instanceMembers={instanceMembers}
                  employees={listBoardEmployees}
                  instancePrefix={instancePrefix}
                  onClose={() => {
                    setEditingActivityId(null);
                    setShowListCreate(false);
                  }}
                  onSaved={() => {
                    loadScheduledActivities();
                    if (showListCreate) {
                      setShowListCreate(false);
                      setEditingActivityId(null);
                    }
                  }}
                  onDeleted={() => {
                    setEditingActivityId(null);
                    setShowListCreate(false);
                    loadScheduledActivities();
                  }}
                  onLeadCreated={handleLeadCreated}
                />
              )}

              {listAbsenceDrawer && (
                <PlantafelAbsenceDrawer
                  draft={listAbsenceDrawer}
                  employees={listBoardEmployees}
                  onClose={() => setListAbsenceDrawer(null)}
                  onSaved={() => {
                    setListAbsenceDrawer(null);
                    loadListAbsences();
                  }}
                />
              )}
            </div>
          )}
        </div>
      </main>
      <AppConfirmDialog
        open={deleteTargets.length > 0}
        onClose={() => setDeleteTargets([])}
        onConfirm={handleConfirmDelete}
        title={deleteDialogTitle}
        description={deleteDialogDescription}
        confirmLabel={deleteTargets.length > 1 ? 'Alle löschen' : 'Löschen'}
        destructive
        isLoading={isDeleting}
      />
      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}